/**
 * NOTE: Модуль требует доработки логики календаря.
 * Разработка отложена, так как используется только в модальном окне фильтров,
 * которое отменено на демо от 11.12.2025
 */

import flatpickr from 'flatpickr'
import { Russian } from 'flatpickr/dist/l10n/ru.js'

/**
 * Интерфейс конфигурации календаря
 */
export interface IDatepickerConfig {
  /** Минимальная доступная для выбора дата */
  minDate: Date
  /** Максимальная доступная для выбора дата */
  maxDate: Date
  /** Встроенный режим отображения (всегда видимый календарь) */
  inline?: boolean
  /** Режим выбора: одна дата, несколько или диапазон */
  mode?: 'single' | 'multiple' | 'range'
  /** Формат отображения даты (например, 'd.m.Y' или 'Y-m-d') */
  dateFormat?: string
  /** Закрывать ли календарь автоматически при выборе даты */
  closeOnSelect?: boolean
  /** Настройки текста кнопки */
  button?: {
    text: string
  }
  /** Заранее выбранные даты — например, восстановленные из адреса страницы */
  defaultDate?: string[] | Date[]
  /** Колбэк, вызываемый при изменении выбранных дат */
  onChange?: (_selectedDates: Date[]) => void
}

/**
 * Класс для управления полем выбора даты с использованием Flatpickr
 *
 * Обеспечивает интеграцию с библиотекой flatpickr для выбора дат
 * с поддержкой русской локализации, различных режимов выбора (одна дата/диапазон)
 * и кастомизированного UI
 */
export class Datepicker {
  /** Корневой HTML-элемент контейнера датапикера */
  private container: HTMLElement

  /** Конфигурация календаря */
  private config: IDatepickerConfig

  /** Экземпляр Flatpickr */
  private flatpicker: flatpickr.Instance | null = null

  /** Флаг состояния: открыт ли календарь */
  private isOpen: boolean = false

  /** Кнопка для открытия/закрытия календаря */
  private toggleButton!: HTMLElement

  /** Исходная подпись кнопки — к ней возвращаемся при сбросе */
  private defaultButtonLabel: string | null = null

  /**
   * Создаёт экземпляр Datepicker
   *
   * @param {HTMLElement} container - Контейнер поля даты с атрибутом [data-datepicker]
   * @param {IDatepickerConfig} config - Конфигурация календаря (minDate, maxDate, mode и т.д.)
   */
  constructor(container: HTMLElement, config: IDatepickerConfig) {
    this.container = container

    this.config = {
      inline: true,
      mode:
        (this.container.dataset.type as 'single' | 'multiple' | 'range') ||
        'single',
      ...config
    }

    this.init()
  }

  /**
   * Инициализирует календарь
   *
   * Находит кнопку toggle, инициализирует Flatpickr
   * и устанавливает обработчики событий
   *
   * @private
   */
  private init(): void {
    this.toggleButton = this.container.querySelector<HTMLElement>(
      '[data-datepicker-button]'
    ) as HTMLElement
    this.toggleButton.setAttribute('aria-expanded', 'false')

    this.initFlatpicker()

    this.toggleButton.addEventListener('click', this.onToggleButtonClick)
    this.container.addEventListener('click', this.onClickInside)
    document.addEventListener(
      'dropdown:open',
      this.onDropdownOpen as EventListener
    )
  }

  /**
   * Инициализирует экземпляр Flatpickr
   * Настраивает календарь с русской локализацией, применяет конфигурацию
   * и кастомизирует кнопки навигации (prev/next) с SVG иконками
   * @private
   */
  private async initFlatpicker(): Promise<void> {
    const content = this.container.querySelector(
      '[data-datepicker-content]'
    ) as HTMLElement

    this.flatpicker = flatpickr(content, {
      locale: Russian,
      mode: this.config.mode,
      allowInput: true,
      dateFormat: this.config.dateFormat || 'd.m.Y',
      minDate:
        this.container.dataset.minDate || this.config.minDate || new Date(),
      maxDate: this.container.dataset.maxDate || this.config.maxDate,
      inline: this.config.inline,
      closeOnSelect: this.config.closeOnSelect ?? true,
      disableMobile: false,
      monthSelectorType: 'static',
      defaultDate: this.config.defaultDate
    })

    const prevButton = this.flatpicker.prevMonthNav
    prevButton.innerHTML = '<svg><use href="#chevron-left"></use></svg> '

    const nextButton = this.flatpicker.nextMonthNav
    nextButton.innerHTML = '<svg><use href="#chevron-right"></use></svg> '
  }

  /**
   * Меняет подпись кнопки календаря.
   *
   * Пустая строка возвращает исходную подпись — ту, что отрисовал шаблон.
   *
   * @param {string} label - Новая подпись.
   */
  public setButtonLabel(label: string): void {
    const target = this.toggleButton.querySelector<HTMLElement>(
      '.datepicker__button-label'
    )

    if (!target) return

    if (this.defaultButtonLabel === null) {
      this.defaultButtonLabel = target.textContent?.trim() || ''
    }

    target.textContent = label || this.defaultButtonLabel
  }

  /**
   * Обработчик клика внутри компонента
   * @param {MouseEvent} event - Событие клика
   */
  private onClickInside = (event: MouseEvent): void => {
    event.stopPropagation()

    const target = event.target as HTMLElement
    if (target.closest('[data-datepicker-reset]')) {
      this.reset()
    }

    if (target.closest('[data-datepicker-apply]')) {
      this.apply()
    }
  }

  /**
   * Обработчик клика вне компонента
   * @param {MouseEvent} event - Событие клика
   */
  private onClickOutside = (event: MouseEvent): void => {
    const target = event.target as HTMLElement

    if (this.isOpen && !target.closest('[data-datepicker]')) {
      this.close()
    }
  }

  /**
   * Обработчик нажатия клавиши Escape
   * @param {KeyboardEvent} event - Событие нажатия клавиши
   */
  private onEscapePress = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && this.isOpen) {
      this.close()
    }
  }

  /**
   * Закрывает календарь при открытии другого dropdown
   * @param {Event} event - Событие открытия dropdown
   */
  private onDropdownOpen = (event: Event): void => {
    const customEvent = event as CustomEvent<{ element?: HTMLElement }>
    const dropdownElement = customEvent.detail?.element

    if (!this.isOpen || !dropdownElement) {
      return
    }

    if (!this.container.contains(dropdownElement)) {
      this.close()
    }
  }

  /**
   * Обработчик клика по кнопке переключения календаря
   * @param {MouseEvent} event - Событие клика
   */
  private onToggleButtonClick = (event: Event): void => {
    event.preventDefault()

    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  /**
   * Открывает календарь
   * @return {void}
   */
  public open(): void {
    if (this.isOpen) return

    this.isOpen = true
    this.container.classList.add('is-open')
    this.toggleButton.setAttribute('aria-expanded', 'true')
    this.toggleButton.classList.add('is-active')

    document.dispatchEvent(
      new CustomEvent('datepicker:open', {
        detail: {
          element: this.container
        }
      })
    )

    document.addEventListener('click', this.onClickOutside)
    document.addEventListener('keydown', this.onEscapePress)
  }

  /**  * Закрывает календарь
   * @return {void}
   */
  public close(): void {
    if (!this.isOpen) return

    this.isOpen = false
    this.container.classList.remove('is-open')
    this.toggleButton.setAttribute('aria-expanded', 'false')
    this.toggleButton.classList.remove('is-active')

    document.removeEventListener('click', this.onClickOutside)
    document.removeEventListener('keydown', this.onEscapePress)
  }

  /**
   * Сбрасывает выбранные даты и закрывает календарь
   */
  private reset(): void {
    this.flatpicker?.clear()
    this.setButtonLabel('')
    this.close()
  }

  /**
   * Применяет выбранные даты, вызывая колбэк onChange и закрывая календарь
   */
  private apply(): void {
    this.close()

    if (this.config.onChange && this.flatpicker) {
      this.config.onChange(this.flatpicker.selectedDates)
    }
  }
}
